import Icon from '@ant-design/icons';
import { Button } from 'antd';
import { SetStateAction, Dispatch, FC } from 'react';
import { useParams } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import { toPng } from 'html-to-image';

import { useContextNotifications } from '../../../context/notifications';
import { useContextReports } from '../../../context/reports';
import { useGetLoanOfficerAvatar, useImageToBase64 } from '../../../hooks/reports';
import { Download } from '../../../components/Common/Icon';
import { IReportRow } from '../../../types/reports';

interface IDownloadReportPdfButtonProps {
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  onCancel?: () => void;
}

const DownloadReportPdfButton: FC<IDownloadReportPdfButtonProps> = ({ isLoading, setIsLoading, onCancel }) => {
  const { id: reportId } = useParams();
  const { report, form, setIsBorrower } = useContextReports();
  const { openNotification } = useContextNotifications();
  const { fetch: getLoanOfficerAvatar } = useGetLoanOfficerAvatar();
  const { fetch: fetchImageToBase64 } = useImageToBase64();

  const handleDownloadPdf = async () => {
    setIsLoading(true);
    setIsBorrower?.(true);

    const fullHeight = 9500;
    const fullWidth = 1126;
    const pageHeight = (842 * fullWidth) / 595; // aspect ratio as A4
    const reportView = form?.getFieldValue('reportView') as IReportRow;
    const reportViewArray = Object.values(reportView);
    const activeSections = reportViewArray.filter((section) => section?.active).sort((a, b) => a.order - b.order);
    const activeWelcomeMessageSection = activeSections.find((section) => section.name === 'welcome_message');

    // eslint-disable-next-line new-cap
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'px',
      format: [pageHeight, fullWidth],
      putOnlyUsedFonts: true,
    });

    // eslint-disable-next-line no-promise-executor-return
    new Promise((resolve) => setTimeout(resolve, 4000))
      .then(async () => {
        const reportAvatar = document.querySelectorAll("img[alt='User Avatar']");
        const originalSrc = reportAvatar[0]?.getAttribute('src');

        const data = await getLoanOfficerAvatar(undefined, reportId);
        const avatarBase64 = data?.data.avatarBase64;

        if (reportAvatar && originalSrc && avatarBase64) {
          reportAvatar.forEach((element) => {
            element.setAttribute('src', avatarBase64);
          });
        }

        const companyLogo = document.getElementById('companyLogo');

        if (companyLogo) {
          const img = companyLogo.getElementsByTagName('img')[0];
          const companyLogoSrc = img.getAttribute('src');

          if (companyLogoSrc !== '/logo.png' && companyLogoSrc) {
            // eslint-disable-next-line @typescript-eslint/no-shadow
            const data = await fetchImageToBase64({ url: companyLogoSrc });

            img.setAttribute('src', data?.data?.dataUri || companyLogoSrc);
          }
        }

        const loans = document.querySelectorAll('.loan');

        loans?.forEach((loan) => {
          // eslint-disable-next-line no-param-reassign
          (loan as HTMLElement).style.minWidth = '206px';
        });

        const loanDetails = document.getElementById('loanDetails');

        if (loanDetails) {
          loanDetails.style.minWidth = '250px';
          loanDetails.style.width = '250px';
        }

        const loanComparisonsHelpIcon = document.getElementById('loanComparisonsHelpIcon');

        if (loanComparisonsHelpIcon) {
          loanComparisonsHelpIcon.style.display = 'none';
        }

        const loanFieldHelpIcons = document.querySelectorAll('.loanFieldHelpIcon');

        loanFieldHelpIcons?.forEach((el) => {
          // eslint-disable-next-line no-param-reassign
          (el as HTMLElement).style.display = 'none';
        });

        const elements: Array<HTMLElement> = [];

        const loanInformation = document.getElementById('loanInformation');

        if (loanInformation) {
          elements.push(loanInformation);
        }

        const loanInformationDivider = document.querySelector('.loanInformationDivider');

        if (loanInformationDivider) {
          elements.push(loanInformationDivider as any);
        }

        if (activeWelcomeMessageSection) {
          const element = document.getElementById(activeWelcomeMessageSection.name);

          if (element) {
            elements.push(element);
          }
        }

        const loansTable = document.getElementById('loansTable');

        if (loansTable) {
          loansTable.style.marginTop = '20px';
          elements.push(loansTable);
        }

        for (let i = 0; i < activeSections.length; i++) {
          const section = activeSections[i];
          const element = document.getElementById(section.name);

          if (element && section.name !== 'welcome_message' && section.name !== 'vidoeWalk') {
            elements.push(element);

            if (section.name === 'taxAdvantage') {
              const taxAdvantageToggle = document.getElementById('taxAdvantageToggle');

              if (taxAdvantageToggle) {
                taxAdvantageToggle.style.display = 'none';
              }
            }

            if (section.name === 'buyDownCalculator') {
              const buyDownCalculatorToggle = document.getElementById('buyDownCalculatorToggle');

              if (buyDownCalculatorToggle) {
                buyDownCalculatorToggle.style.opacity = '0';
              }
            }
          }
        }

        const reportsElement = document.getElementById('reportToPdf');
        const originalStyle = reportsElement ? reportsElement.getAttribute('style') : '';

        if (reportsElement)
          reportsElement.setAttribute(
            'style',
            `${originalStyle}; width:${fullWidth}px; height:${fullHeight}px ; overflow: visible`
          );

        try {
          if (!reportsElement) return;

          let currentY = 0; // Track current Y position on the page

          for (let i = 0; i < elements.length; i++) {
            const element = elements[i];
            // Get the actual height of the element
            const elementHeight = element.getBoundingClientRect().height;
            // Scale the element height to match PDF dimensions
            const scaledHeight = (elementHeight * fullWidth) / element.getBoundingClientRect().width;

            // Check if the section fits on the current page
            if (currentY + scaledHeight > pageHeight && currentY !== 0) {
              pdf.addPage();
              currentY = 0; // Reset Y position for the new page
            }

            // Capture the section as an image
            // eslint-disable-next-line no-await-in-loop
            const dataUrl = await toPng(elements[i], {
              cacheBust: true,
              skipAutoScale: true,
              width: fullWidth,
              style: {
                background: 'white',
                width: `${fullWidth}px`,
              },
            });

            // Add the image to the PDF at the current Y position
            pdf.addImage(dataUrl, 'PNG', 0, currentY, fullWidth, scaledHeight, undefined, 'MEDIUM');

            // Update the current Y position
            currentY += scaledHeight;
          }

          const base64Pdf = pdf.output('datauristring').split(',')[1];

          pdf.save(`${report?.name ?? 'Report'}`);
        } catch (err) {
          openNotification?.({
            message: `Error while generating PDF: ${err}`,
            type: 'error',
          });
        } finally {
          // eslint-disable-next-line @typescript-eslint/no-shadow
          const reportsElement = document.getElementById('reportToPdf');

          if (reportsElement) {
            reportsElement.style.width = '100%';
            reportsElement.style.height = '100%';
          }

          const taxAdvantageToggle = document.getElementById('taxAdvantageToggle');

          if (taxAdvantageToggle) {
            taxAdvantageToggle.style.display = 'block';
          }

          const buyDownCalculatorToggle = document.getElementById('buyDownCalculatorToggle');

          if (buyDownCalculatorToggle) {
            buyDownCalculatorToggle.style.opacity = '1';
          }

          // eslint-disable-next-line @typescript-eslint/no-shadow
          const loanComparisonsHelpIcon = document.getElementById('loanComparisonsHelpIcon');

          if (loanComparisonsHelpIcon) {
            loanComparisonsHelpIcon.style.display = 'block';
          }

          // eslint-disable-next-line @typescript-eslint/no-shadow
          const loanFieldHelpIcons = document.querySelectorAll('.loanFieldHelpIcon');

          loanFieldHelpIcons.forEach((el) => {
            // eslint-disable-next-line no-param-reassign
            (el as HTMLElement).style.display = 'block';
          });

          // eslint-disable-next-line @typescript-eslint/no-shadow
          const loans = document.querySelectorAll('.loan');

          loans?.forEach((loan) => {
            // eslint-disable-next-line no-param-reassign
            (loan as HTMLElement).style.minWidth = '280px';
          });

          // eslint-disable-next-line @typescript-eslint/no-shadow
          const loanDetails = document.getElementById('loanDetails');

          if (loanDetails) {
            loanDetails.style.minWidth = '280px';
            loanDetails.style.width = '280px';
          }

          // eslint-disable-next-line @typescript-eslint/no-shadow
          const loansTable = document.getElementById('loansTable');

          if (loansTable) {
            loansTable.style.marginTop = '0';
          }

          setIsLoading(false);
          setIsBorrower?.(false);
          onCancel?.();
        }
      })
      .finally(() => {
        setIsLoading(false);
        setIsBorrower?.(false);
        onCancel?.();
      });
  };

  return (
    <>
      <Button
        type="primary"
        size="large"
        className="d-m-none"
        onClick={handleDownloadPdf}
        download
        disabled={isLoading}
      >
        Download PDF
      </Button>
      <Button
        type="default"
        size="large"
        onClick={handleDownloadPdf}
        icon={<Icon component={Download} />}
        style={{ minWidth: 40, alignItems: 'center', justifyContent: 'center' }}
        className="d-lg-none d-m-flex btn-default-dark-blue"
        download
      />
    </>
  );
};

export default DownloadReportPdfButton;
